import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { planService } from '../../services/plan.service';
import type { Plan } from '../../types';
import { PlanStatus } from '../../types';
import { formatINR } from '../../lib/currency';

// ── form ──────────────────────────────────────────────────────────────────────
type FeatureKey =
  | 'gps_tracking' | 'geofencing' | 'visit_management'
  | 'api_access' | 'custom_roles' | 'multi_branch' | 'priority_support';

interface PlanFormValues {
  name: string;
  employeeLimit: number;
  monthlyPrice: number;
  yearlyPrice: number;
  status: PlanStatus;
  reporting: string;
  data_retention_days: number;
  gps_tracking: boolean;
  geofencing: boolean;
  visit_management: boolean;
  api_access: boolean;
  custom_roles: boolean;
  multi_branch: boolean;
  priority_support: boolean;
}

const FEATURE_ITEMS: Array<{ key: FeatureKey; label: string; desc: string }> = [
  { key: 'gps_tracking',    label: 'GPS Tracking',      desc: 'Real-time employee location' },
  { key: 'geofencing',      label: 'Geofencing',        desc: 'Zone-based alerts & rules' },
  { key: 'visit_management',label: 'Visit Management',  desc: 'Schedule & track visits' },
  { key: 'api_access',      label: 'API Access',        desc: 'REST API integration' },
  { key: 'custom_roles',    label: 'Custom Roles',      desc: 'Role-based permissions' },
  { key: 'multi_branch',    label: 'Multi Branch',      desc: 'Multiple office locations' },
  { key: 'priority_support',label: 'Priority Support',  desc: 'SLA-backed support' },
];

const emptyForm: PlanFormValues = {
  name: '', employeeLimit: 25, monthlyPrice: 2499, yearlyPrice: 24990,
  status: PlanStatus.ACTIVE, reporting: 'basic', data_retention_days: 90,
  gps_tracking: true, geofencing: true, visit_management: true,
  api_access: false, custom_roles: false, multi_branch: false, priority_support: false,
};

function formFromPlan(p: Plan): PlanFormValues {
  const f = p.features ?? {};
  return {
    name: p.name, employeeLimit: p.employeeLimit,
    monthlyPrice: p.monthlyPrice, yearlyPrice: p.yearlyPrice,
    status: (p.status as PlanStatus) ?? PlanStatus.ACTIVE,
    reporting: String(f.reporting ?? 'basic'),
    data_retention_days: Number(f.data_retention_days ?? 90),
    gps_tracking: Boolean(f.gps_tracking ?? true),
    geofencing: Boolean(f.geofencing ?? true),
    visit_management: Boolean(f.visit_management ?? true),
    api_access: Boolean(f.api_access),
    custom_roles: Boolean(f.custom_roles),
    multi_branch: Boolean(f.multi_branch),
    priority_support: Boolean(f.priority_support),
  };
}

function featuresToRecord(v: PlanFormValues): Record<string, unknown> {
  return {
    gps_tracking: v.gps_tracking, geofencing: v.geofencing,
    visit_management: v.visit_management, api_access: v.api_access,
    custom_roles: v.custom_roles, multi_branch: v.multi_branch,
    priority_support: v.priority_support,
    reporting: v.reporting, data_retention_days: Number(v.data_retention_days),
  };
}

function enabledFeatures(p: Plan): string[] {
  const f = p.features ?? {};
  const out: string[] = [];
  if (f.gps_tracking)    out.push('GPS Tracking');
  if (f.geofencing)      out.push('Geofencing');
  if (f.visit_management)out.push('Visit Management');
  if (f.api_access)      out.push('API Access');
  if (f.custom_roles)    out.push('Custom Roles');
  if (f.multi_branch)    out.push('Multi Branch');
  if (f.priority_support)out.push('Priority Support');
  if (f.reporting)       out.push(`${String(f.reporting).charAt(0).toUpperCase() + String(f.reporting).slice(1)} Reports`);
  return out;
}

// Card gradient per index
const GRADIENTS = [
  'linear-gradient(135deg,#E8EEF8 0%,#D7E3F4 100%)',
  'linear-gradient(135deg,#E8F3F6 0%,#D3E8EE 100%)',
  'linear-gradient(135deg,#EAF4EE 0%,#D8EADF 100%)',
];
const ACCENT_COLORS = ['#2563EB', '#0E7490', '#2F9E6A'];

// ── Page ──────────────────────────────────────────────────────────────────────
export function PlansPage() {
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [editing,      setEditing]      = useState<Plan | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Plan | null>(null);
  const qc = useQueryClient();

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => planService.getAll(),
  });

  const { register, handleSubmit, reset, setValue, watch } = useForm<PlanFormValues>({ defaultValues: emptyForm });

  const saveMutation = useMutation({
    mutationFn: (values: PlanFormValues) => {
      const payload = {
        name: values.name.trim(),
        employeeLimit: Number(values.employeeLimit),
        monthlyPrice: Number(values.monthlyPrice),
        yearlyPrice: Number(values.yearlyPrice),
        status: values.status,
        features: featuresToRecord(values),
      };
      return editing ? planService.update(editing.id, payload) : planService.create(payload);
    },
    onSuccess: () => { toast.success(editing ? 'Plan updated' : 'Plan created'); qc.invalidateQueries({ queryKey: ['plans'] }); closeDialog(); },
    onError: (e: Error) => toast.error(e.message || 'Failed to save plan'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PlanStatus }) => planService.update(id, { status }),
    onSuccess: () => { toast.success('Plan status updated'); qc.invalidateQueries({ queryKey: ['plans'] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  function closeDialog() { setDialogOpen(false); setEditing(null); reset(emptyForm); }
  function openCreate() { setEditing(null); reset(emptyForm); setDialogOpen(true); }
  function openEdit(p: Plan) { setEditing(p); reset(formFromPlan(p)); setDialogOpen(true); }

  const sorted = useMemo(() => [...plans].sort((a, b) => a.monthlyPrice - b.monthlyPrice), [plans]);
  const popularId = sorted.find((p) => p.name.toLowerCase().includes('professional'))?.id ?? sorted[1]?.id;

  return (
    <Box>
      {/* ── Header ───────────────────────────────────────── */}
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
          New Plan
        </Button>
      </Stack>

      {isLoading ? (
        <LoadingSpinner message="Loading plans…" />
      ) : sorted.length === 0 ? (
        <Box
          sx={{
            mt: 4, p: 8, textAlign: 'center', borderRadius: '20px',
            border: '2px dashed', borderColor: 'divider', bgcolor: 'background.paper',
          }}
        >
          <StarRoundedIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} mb={0.75}>No plans yet</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>Create your first subscription plan to start onboarding tenants.</Typography>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>Create Plan</Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {sorted.map((plan, idx) => {
            const active  = String(plan.status) === PlanStatus.ACTIVE;
            const popular = plan.id === popularId;
            const accent  = ACCENT_COLORS[idx % ACCENT_COLORS.length];
            const grad    = GRADIENTS[idx % GRADIENTS.length];
            const features = enabledFeatures(plan);

            return (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    borderRadius: '20px',
                    border: popular ? `2px solid ${accent}` : '1px solid',
                    borderColor: popular ? accent : 'divider',
                    boxShadow: popular
                      ? `0 16px 40px ${alpha(accent, 0.18)}`
                      : '0 4px 16px rgba(15,23,42,0.07)',
                    opacity: active ? 1 : 0.65,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: popular
                        ? `0 24px 56px ${alpha(accent, 0.24)}`
                        : '0 12px 32px rgba(15,23,42,0.12)',
                    },
                  }}
                >
                  {popular && (
                    <Chip
                      icon={<StarRoundedIcon sx={{ fontSize: '14px !important' }} />}
                      label="Most Popular"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -13,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: accent,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 26,
                        boxShadow: `0 4px 12px ${alpha(accent, 0.4)}`,
                        '& .MuiChip-icon': { color: '#fff' },
                      }}
                    />
                  )}

                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3, pt: popular ? 4 : 3 }}>
                    {/* Plan header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                      <Box
                        sx={{
                          px: 2, py: 0.75, borderRadius: '10px',
                          background: grad,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: accent }}>{plan.name}</Typography>
                      </Box>
                      <Chip
                        label={active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          fontWeight: 700, fontSize: '0.68rem', height: 22,
                          bgcolor: active ? '#ECFDF5' : '#F8FAFC',
                          color: active ? '#065F46' : '#64748B',
                        }}
                      />
                    </Stack>

                    {/* Price */}
                    <Stack direction="row" alignItems="flex-end" spacing={0.5} mb={0.5}>
                      <Typography sx={{ fontSize: '2.25rem', fontWeight: 900, color: 'text.primary', lineHeight: 1, letterSpacing: '-0.04em' }}>
                        {formatINR(plan.monthlyPrice)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}>/month</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
                      or {formatINR(plan.yearlyPrice)}/year
                      {plan.yearlyPrice && plan.monthlyPrice && (
                        <Typography component="span" sx={{ ml: 0.5, color: 'success.main', fontWeight: 700, fontSize: '0.72rem' }}>
                          (save {Math.round(100 - (plan.yearlyPrice / (plan.monthlyPrice * 12)) * 100)}%)
                        </Typography>
                      )}
                    </Typography>

                    <Divider sx={{ mb: 2.5 }} />

                    {/* Seats */}
                    <Typography
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        fontSize: '0.875rem', fontWeight: 700, color: 'text.primary', mb: 2,
                      }}
                    >
                      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, display: 'inline-block' }} />
                      {plan.employeeLimit === -1 ? 'Unlimited employees' : `Up to ${plan.employeeLimit} employees`}
                    </Typography>

                    {/* Features */}
                    <Stack spacing={1} flex={1}>
                      {features.slice(0, 7).map((label) => (
                        <Stack key={label} direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 18, height: 18, borderRadius: '6px',
                              bgcolor: alpha(accent, 0.12),
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}
                          >
                            <CheckRoundedIcon sx={{ fontSize: 12, color: accent, fontWeight: 700 }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
                        </Stack>
                      ))}
                      {features.length === 0 && (
                        <Typography variant="body2" color="text.disabled">No features configured</Typography>
                      )}
                    </Stack>

                    <Divider sx={{ mt: 2.5, mb: 2 }} />

                    {/* Actions */}
                    <Stack direction="row" spacing={1}>
                      <Button
                        fullWidth
                        variant={popular ? 'contained' : 'outlined'}
                        startIcon={<EditRoundedIcon />}
                        onClick={() => openEdit(plan)}
                        size="small"
                        sx={popular ? {} : { borderColor: alpha(accent, 0.4), color: accent, '&:hover': { bgcolor: alpha(accent, 0.06), borderColor: accent } }}
                      >
                        Edit Plan
                      </Button>
                      <Tooltip title={active ? 'Deactivate plan' : 'Activate plan'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleMutation.mutate({ id: plan.id, status: active ? PlanStatus.INACTIVE : PlanStatus.ACTIVE })}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '10px',
                            color: active ? 'warning.main' : 'success.main',
                            '&:hover': { bgcolor: active ? '#FFFBEB' : '#ECFDF5', borderColor: active ? '#D97706' : '#059669' },
                          }}
                        >
                          <PowerSettingsNewRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Create / Edit dialog ─────────────────────────── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))}>
          <DialogTitle>{editing ? 'Edit Plan' : 'New Plan'}</DialogTitle>
          <Divider />
          <DialogContent>
            <Grid container spacing={2.5} pt={1}>
              {/* Basic info */}
              <Grid item xs={12} md={6}>
                <TextField label="Plan name" fullWidth required {...register('name', { required: true })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Employee limit (−1 = unlimited)" type="number" fullWidth required {...register('employeeLimit', { valueAsNumber: true })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Monthly price (₹)" type="number" inputProps={{ step: '1', min: 0 }} fullWidth required {...register('monthlyPrice', { valueAsNumber: true })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Yearly price (₹)" type="number" inputProps={{ step: '1', min: 0 }} fullWidth required {...register('yearlyPrice', { valueAsNumber: true })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Data retention (days)" type="number" fullWidth {...register('data_retention_days', { valueAsNumber: true })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Reporting level"
                  fullWidth
                  SelectProps={{ native: true }}
                  {...register('reporting')}
                >
                  <option value="basic">Basic</option>
                  <option value="advanced">Advanced</option>
                  <option value="enterprise">Enterprise</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={watch('status') === PlanStatus.ACTIVE}
                      onChange={(e) => setValue('status', e.target.checked ? PlanStatus.ACTIVE : PlanStatus.INACTIVE)}
                    />
                  }
                  label={<Typography fontWeight={600} fontSize="0.875rem">Plan is active</Typography>}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="text.primary">Features</Typography>
                <Grid container spacing={1}>
                  {FEATURE_ITEMS.map((f) => (
                    <Grid item xs={12} sm={6} md={4} key={f.key}>
                      <Box
                        sx={{
                          p: 1.5, borderRadius: '12px',
                          border: '1px solid',
                          borderColor: watch(f.key) ? 'primary.main' : 'divider',
                          bgcolor: watch(f.key) ? '#EFF6FF' : 'background.default',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                          control={
                            <Switch
                              checked={Boolean(watch(f.key))}
                              onChange={(e) => setValue(f.key, e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box ml={0.5}>
                              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{f.label}</Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{f.desc}</Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} color="inherit" variant="outlined">Cancel</Button>
            <Button type="submit" variant="contained" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create Plan'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate plan?"
        message={`"${deactivateTarget?.name}" will be marked inactive and hidden from new tenant sign-ups.`}
        confirmLabel="Deactivate"
        confirmColor="error"
        isLoading={toggleMutation.isPending}
        onCancel={() => setDeactivateTarget(null)}
        onConfirm={() => deactivateTarget && toggleMutation.mutate({ id: deactivateTarget.id, status: PlanStatus.INACTIVE })}
      />
    </Box>
  );
}
